/**
 * Plain-language definitions for the Overview figures.
 *
 * Kyu, 2026-08-27, pointing at Touches: "might sound jargon to Alex, or any
 * people who are not in the weeds."
 *
 * The copy is kept here rather than inline in the view for one reason that
 * matters: these sentences have to stay true to how the figures are actually
 * computed in dashboardSections.ts and activityStats.ts. Keeping them in one
 * named map means a change to a definition has one obvious place to land, and
 * a test can assert every rendered tile has one.
 *
 * Two of these carry a fact a reader would otherwise get wrong, and neither is
 * decoration:
 *   - Meetings is a SUBSET of Touches, not a sibling of it. A reader adding the
 *     two together double-counts.
 *   - The pipeline figures are current-state counts. They do not move when the
 *     range switch moves, and somebody comparing "This Week" to "This Month"
 *     will otherwise read that as nothing having happened.
 */
export const METRIC_HINTS = {
  arrived:
    'New leads that came in during this date range, counted from the day the lead was created.',
  touches:
    'Every time the team reached out in this date range — emails, calls and meetings. Read from each lead’s activity log, not from its current status. Meetings are included in this number.',
  replies:
    'Leads who wrote back in this date range. Counted once per lead per day, so a reply recorded in two places does not count twice.',
  meetings:
    'Calls and meetings held in this date range. These are also counted inside Touches, so do not add the two together.',
  converted:
    'Leads that became a deal in this date range, counted from the day the deal was created.',
  awaitingFirstTouch:
    'Leads nobody has contacted yet. This is where leads sit right now — it does not change when you change the date range above.',
  inTalks:
    'Leads in live conversation with us. This is where leads sit right now — it does not change when you change the date range above.',
  activeDeals:
    'Leads that now have a deal attached. This is where leads sit right now — it does not change when you change the date range above.',
  won: 'Leads whose deal was won. Read off the linked deal, not off the lead — mark a deal Won in Deals and it appears here, with nothing else to update. Same rule as the Won band on the Pipeline, so the two always agree. This is where leads sit right now; it does not change when you change the date range above.',
} as const;

export type MetricHintKey = keyof typeof METRIC_HINTS;

/**
 * The same idea one level up: an explanation on each GROUP of figures, not
 * only on each figure.
 *
 * Kyu, 31 Aug, after three failed explanations of the difference between the
 * two runs: "I'm the one making this with you... and I don't understand what
 * I'm looking at. And that is a big problem."
 *
 * He is right, and the fix is not a better guide page. Nine tiles each carried
 * a definition while the two headings that group them carried none — so the
 * question a reader actually asks FIRST, what is the difference between these
 * two halves, was the only one the page refused to answer. These are that
 * answer, put where the question gets asked.
 *
 * Written to his own sentence, which is the plainest either of us managed: one
 * side is what we did, the other is who we have.
 */
export const GROUP_HINTS = {
  activity:
    'What we did between these dates. Every number here is something that happened — a lead arrived, we sent an email, someone wrote back. Change the date range above and all of them change with it.',
  pipeline:
    'Who we have right now. A headcount of where leads are sitting today, whatever dates are showing on the left — someone counted here as In talks may have replied months ago. These four never change when you change the date range.',
  whatMoved:
    'The same activity as the left-hand figures above, by name instead of by count: the individual leads that replied, booked a meeting or became a deal inside these dates. Click a name to open that lead.',
} as const;

export type GroupHintKey = keyof typeof GROUP_HINTS;
